import { defineStore } from 'pinia'
import { fetchMe, logout as apiLogout, requestOtp, verifyOtp } from '@/api/auth'
import { getMyProfile, updateMyProfile } from '@/api/profile'
import type { FishermanProfile, ProfileUpdate } from '@/api/types'
import {
  clearSession,
  isSessionActive,
  sessionDisplayName,
  sessionFishermanId,
  setSession,
} from './session'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    profile: null as FishermanProfile | null,
    profileLoading: false,
    /** The masked phone + expiry shown while waiting for the SMS. */
    pendingPhone: null as string | null,
    pendingExpiresAt: null as string | null,
  }),

  getters: {
    isAuthenticated: () => isSessionActive.value,
    fishermanId: () => sessionFishermanId.value,
    displayName: () => sessionDisplayName.value,
  },

  actions: {
    /** Step 1 of OTP login. Throws ApiError on validation/rate-limit failure. */
    async requestCode(phone: string) {
      const result = await requestOtp(phone)
      this.pendingPhone = phone
      this.pendingExpiresAt = result.expires_at
      return result
    },

    /** Step 2. On success the session is persisted and this.profile is warmed. */
    async verifyCode(phone: string, code: string) {
      const session = await verifyOtp(phone, code)
      setSession({
        token: session.token,
        expiresAt: session.expires_at,
        fishermanId: session.fisherman_id ?? '',
        displayName: session.display_name,
      })
      this.pendingPhone = null
      this.pendingExpiresAt = null
      await this.loadProfile().catch(() => {
        // A failed profile fetch right after login should not undo a
        // successful sign-in — the home screen retries it on its own.
      })
      return session
    },

    async loadProfile() {
      this.profileLoading = true
      try {
        this.profile = await getMyProfile()
      } finally {
        this.profileLoading = false
      }
    },

    async saveProfile(patch: ProfileUpdate) {
      const result = await updateMyProfile(patch)
      this.profile = result.fisherman
      return result
    },

    /** Confirms the token is still good server-side (used on cold start). */
    async confirmSession() {
      if (!isSessionActive.value) return false
      try {
        await fetchMe()
        return true
      } catch {
        return false
      }
    },

    async logout() {
      try {
        await apiLogout()
      } finally {
        clearSession()
        this.profile = null
        this.pendingPhone = null
        this.pendingExpiresAt = null
      }
    },
  },
})
